<template>
  <b-container fluid>
    <b-row class="mb-2">
      <b-col class="px-sm-3 px-0">
        <h1>Menu Items</h1>
      </b-col>
      <b-col v-if="brandAdmin" class="text-right px-sm-3 px-0">
        <b-button
          class="m-1"
          variant="primary"
          @click="$router.push('items/create')"
          >Add Item</b-button
        >
      </b-col>
    </b-row>
    <b-row>
      <b-col class="px-sm-3 px-0">
        <b-overlay :show="loading" no-wrap fixed />
        <b-form inline class="mb-2" @submit.prevent="applyFilters">
          <b-form-input
            v-model="queryFilters.search"
            placeholder="Search by name, id, description or item id"
            title="Search by name, id, description or item id"
            class="mr-2 mb-2 single-input"
          />
          <custom-search-field
            v-if="brandCount > 1 || hubCount"
            v-model="queryFilters.brand"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by brand'"
            :path-to-search="'brands'"
          />
          <custom-search-field
            v-model="queryFilters.menu"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by menu'"
            :path-to-search="'menus'"
            :query-params="{
              status: 'active,inactive',
            }"
          />
          <!-- <b-form-group class="custom-search-input mr-2 mb-2">
            <multiselect
              v-model="queryFilters.availability"
              select-label=""
              open-direction="bottom"
              :options="availabilityOptions"
              track-by="value"
              deselect-label="remove"
              :searchable="false"
              :close-on-select="true"
              :show-no-results="false"
              :hide-selected="false"
              label="name"
              placeholder="Filter by availability"
            />
          </b-form-group> -->
          <custom-search-field
            v-if="kitchenCount > 1"
            v-model="queryFilters.kitchen"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by kitchen'"
            :path-to-search="'fulfillment-centers'"
          />
          <b-button type="submit" variant="primary" class="mb-2"
            >Filter</b-button
          >
        </b-form>
      </b-col>
    </b-row>
    <b-row class="overflow-auto">
      <b-col class="px-sm-3 px-0">
        <b-table
          hover
          no-local-sorting
          small
          :fields="fields"
          :items="data"
          :busy="busy"
          @sort-changed="onSortingChanged"
          @row-clicked="onRowClicked"
        >
          <template #head(isAvailable)>
            <div class="d-flex">
              Available
              <span id="menu-items-table-availability" class="icon ml-2">
                <img
                  id="hours-tooltip"
                  :src="asset('/imgs/icons/info.svg')"
                  alt="hours info"
                  class="mb-1"
                />
              </span>
            </div>
            <b-tooltip target="menu-items-table-availability" triggers="hover">
              This column indicates if customers can currently order the menu
              item. If unavailable, it does not display on the menu.
              Availability may be due to item status, menu item status, brand
              status, or kitchen status.
            </b-tooltip>
          </template>
          <template #cell(activeName)="row">
            <router-link :to="`/items/${row.item.item_id}`">
              {{ row.item.activeName }}
            </router-link>
          </template>
          <template #cell(activePrice)="row">
            {{ (row.item.activePrice / 100) | currency }}
          </template>
          <template #cell(isAvailable)="row">
            <template v-if="row.item.isAvailable">Available</template>
            <template v-else>Unavailable</template>
          </template>
          <template #cell(fulfillment_center)="row">
            <template v-if="row.item.fulfillment_center">
              {{ row.item.fulfillment_center.name }}
            </template>
          </template>
          <template #cell(menu)="row">
            <template v-if="row.item.menu">
              {{ row.item.menu.name }}
            </template>
          </template>
          <template #cell(brand)="row">
            <template v-if="row.item.brand">
              {{ row.item.brand.name }}
            </template>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <pagination-handler v-if="data.length" :query-data="queryFilters" />
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import tableMixin from '../../mixins/TableMixin';
import ResourceAPI from '../../services/ResourceAPI';

export default {
  name: 'MenuItems',
  mixins: [tableMixin],
  data() {
    return {
      availabilityOptions: [
        {
          value: 'available',
          name: 'Available',
        },
        {
          value: 'unavailable',
          name: 'Unavailable',
        },
      ],
      fields: [
        {
          key: 'activeName',
          label: 'Item',
          sortable: true,
        },
        {
          key: 'isAvailable',
          label: 'Available',
          // sortable: true,
        },
        {
          key: 'activePrice',
          label: 'Price',
          sortable: true,
        },
        {
          key: 'menu',
          label: 'Menu',
          sortable: true,
        },
        {
          key: 'fulfillment_center',
          sortKey: 'fulfillmentCenter',
          label: 'Kitchen',
          sortable: true,
        },
        {
          key: 'brand',
          label: 'Brand',
          sortable: true,
        },
      ],
      data: [],
      rowRouteName: 'brand',
      loading: false,
      exportAlert: false,
    };
  },
  computed: {
    ...mapGetters('session', [
      'brandAdmin',
      'brandCount',
      'hubCount',
      'kitchenCount',
    ]),
  },
  mounted() {
    if (this.kitchenCount === 1) {
      this.removeTableColumn('Kitchen');
    }
  },
  methods: {
    loadResults() {
      this.setRequestData();
      // Format search params for query
      const searchParams = { ...this.queryFilters };
      searchParams.fulfillmentCenter = searchParams.kitchen
        ? searchParams.kitchen.id
        : null;
      searchParams.brand = searchParams.brand ? searchParams.brand.id : null;
      searchParams.menu = searchParams.menu ? searchParams.menu.id : null;
      searchParams.with = 'brand,fulfillmentCenter,menu';
      searchParams.appends = 'activeName,activePrice,isAvailable';
      searchParams.status = 'active,inactive';
      searchParams.menuStatus = 'active,inactive';

      return ResourceAPI.getRecords('menu-items', searchParams)
        .then((data) => {
          this.setResponseData(data);
        })
        .catch((error) => alert(error));
    },

    onRowClicked(record) {
      this.$router.push({
        name: 'item',
        params: { id: String(record.item_id) },
      });
    },
  },
};
</script>
